import { MemoryBus } from "../../interfaces/memory-bus";
import { AddressingMode, Opcode, Flag, Register } from "../../interfaces/op-code";
import { getInstructionExecutor } from "./instructions";
import { getAddressingMode } from "./addressing";
import { padHex, MemoryAccess } from "../../util/helpers";
import { Logger } from "../../util/logger";
import opcodesData from '../../data/opcodes.json';

export class Cpu {
    A: number = 0; // Accumulator
    X: number = 0; // X register
    Y: number = 0; // Y register
    SP: number = 0xFF; // Stack pointer (0x0100–0x01FF)
    PC: number = 0; // Program counter
    SR: number = 0x34; // Status register (N V 1 B D I Z C), init with I=1, unused=1
    private cycles: number = 0; // Cycle counter
    addrResult: { address: number; pageCrossed: boolean } = { address: 0, pageCrossed: false }; // Public for addressing.ts
    public memory: MemoryAccess;
    private opcodes: { [key: number]: { exec: () => void; mode: () => void; bytes: number; cycles: number; pageCross: boolean } } = {};
    private logger: Logger;

    constructor(bus: MemoryBus) {
        this.memory = new MemoryAccess(bus, () => this.SP, (value) => this.SP = value);
        this.logger = new Logger('CPU', 0);
        this.initializeOpcodes();
        this.reset();
    }

    getMemory(): MemoryAccess {
        return this.memory;
    }

    private initializeOpcodes() {
        for (const [opcodeStr, data] of Object.entries(opcodesData.opcodes as { [key: string]: Opcode })) {
            const opcode = parseInt(opcodeStr, 16);
            const { instruction, mode, bytes, cycles, pageCross } = data;

            const execFn = getInstructionExecutor(this, instruction);
            const modeFn = getAddressingMode(this, mode);

            this.opcodes[opcode] = {
                exec: () => {
                    if (['ASL', 'LSR', 'ROL', 'ROR'].includes(instruction) && mode === AddressingMode.Implied) {
                        this.addrResult.address = -1; // Special case for accumulator mode
                        this.logger.log(3, () => `Set accumulator mode for ${instruction}: addr=-1`);
                    }
                    this.logger.log(2, () => `Executing opcode 0x${padHex(opcode, 2)} (${instruction}, ${mode}) at PC=0x${padHex(this.PC, 4)}`);
                    execFn();
                },
                mode: () => {
                    modeFn();
                    this.logger.log(3, () => `Addressing mode ${mode} for opcode 0x${padHex(opcode, 2)}, result address=0x${padHex(this.addrResult.address, 4)}, pageCrossed=${this.addrResult.pageCrossed}`);
                },
                bytes,
                cycles,
                pageCross: pageCross === true,
            };
            this.logger.log(3, () => `Initialized opcode 0x${padHex(opcode, 2)}: ${instruction}, mode=${mode}, bytes=${bytes}, cycles=${cycles}`);
        }
    }

    reset() {
        this.PC = this.memory.read16(0xFFFC); // Reset vector
        this.logger.log(1, () => `CPU Reset: PC=0x${padHex(this.PC, 4)}, SP=0x${padHex(this.SP, 2)}, SR=0x${padHex(this.SR, 2)}`);
        this.SP = 0xFF;
        this.A = this.X = this.Y = 0;
        this.SR = 0x34; // I=1, unused bit 5=1
        this.cycles = 7;
    }

    // Flag helpers
    private updateZN(value: number) {
        const oldSR = this.SR;
        this.SR = (this.SR & ~0x82) | (value === 0 ? Flag.Zero : 0) | (value & Flag.Negative);
        this.logger.log(3, () => `Update ZN flags: value=0x${padHex(value, 2)}, SR=0x${padHex(oldSR, 2)} -> 0x${padHex(this.SR, 2)}`);
    }

    public setFlag(flag: Flag, value: boolean) {
        const oldSR = this.SR;
        this.SR = value ? (this.SR | flag) : (this.SR & ~flag);
        this.logger.log(3, () => `Set flag ${Flag[flag]} to ${value}: SR=0x${padHex(oldSR, 2)} -> 0x${padHex(this.SR, 2)}`);
    }

    getFlag(flag: Flag): boolean {
        const result = (this.SR & flag) !== 0;
        this.logger.log(3, () => `Get flag ${Flag[flag]}: ${result}`);
        return result;
    }

    // Instruction utilities
    public load(register: Register) {
        const value = this.memory.read(this.addrResult.address);
        this.logger.log(3, () => `Load ${register}: addr=0x${padHex(this.addrResult.address, 4)}, value=0x${padHex(value, 2)}`);
        this[register] = value;
        this.logger.log(2, () => `${register}=0x${padHex(this[register], 2)}`);
        this.updateZN(value);
    }

    public store(register: Register) {
        this.logger.log(3, () => `Store ${register}: addr=0x${padHex(this.addrResult.address, 4)}, value=0x${padHex(this[register], 2)}`);
        this.memory.write(this.addrResult.address, this[register]);
    }

    public compare(register: Register) {
        const value = this.memory.read(this.addrResult.address);
        const result = this[register] - value;
        this.logger.log(3, () => `Compare ${register}: addr=0x${padHex(this.addrResult.address, 4)}, value=0x${padHex(value, 2)}, result=0x${padHex(result & 0xFF, 2)}`);
        this.setFlag(Flag.Carry, this[register] >= (value & 0xFF));
        this.updateZN(result & 0xFF);
    }

    public transfer(source: Register, dest: Register, updateFlags: boolean = true) {
        this[dest] = this[source];
        this.logger.log(2, () => `Transfer ${source} to ${dest}: value=0x${padHex(this[dest], 2)}`);
        if (updateFlags && dest !== Register.SP) {
            this.updateZN(this[dest]);
        }
    }

    public increment(register: Register) {
        const oldValue = this[register];
        this[register] = (this[register] + 1) & 0xFF;
        this.logger.log(2, () => `Increment ${register}: 0x${padHex(oldValue, 2)} -> 0x${padHex(this[register], 2)}`);
        this.updateZN(this[register]);
    }

    public decrement(register: Register) {
        const oldValue = this[register];
        this[register] = (this[register] - 1) & 0xFF;
        this.logger.log(2, () => `Decrement ${register}: 0x${padHex(oldValue, 2)} -> 0x${padHex(this[register], 2)}`);
        this.updateZN(this[register]);
    }

    public incrementMemory() {
        const addr = this.addrResult.address;
        const oldValue = this.memory.read(addr);
        const value = (oldValue + 1) & 0xFF;
        this.logger.log(3, () => `Increment memory: addr=0x${padHex(addr, 4)}, value=0x${padHex(oldValue, 2)} -> 0x${padHex(value, 2)}`);
        this.memory.write(addr, value);
        this.updateZN(value);
    }

    public decrementMemory() {
        const addr = this.addrResult.address;
        const oldValue = this.memory.read(addr);
        const value = (oldValue - 1) & 0xFF;
        this.logger.log(3, () => `Decrement memory: addr=0x${padHex(addr, 4)}, value=0x${padHex(oldValue, 2)} -> 0x${padHex(value, 2)}`);
        this.memory.write(addr, value);
        this.updateZN(value);
    }

    public addWithCarry() {
        if (this.getFlag(Flag.Decimal)) this.logger.log(2, () => "Decimal mode set but ignored (NES)");
        const value = this.memory.read(this.addrResult.address);
        const carry = this.getFlag(Flag.Carry) ? 1 : 0;
        const sum = this.A + value + carry;
        const overflow = ((this.A ^ sum) & (value ^ sum) & 0x80) !== 0;
        this.logger.log(3, () => `ADC: A=0x${padHex(this.A, 2)}, value=0x${padHex(value, 2)}, carry=${carry}, sum=0x${padHex(sum & 0xFF, 2)}`);
        this.A = sum & 0xFF;
        this.setFlag(Flag.Carry, sum > 0xFF);
        this.setFlag(Flag.Overflow, overflow);
        this.updateZN(this.A);
        this.logger.log(2, () => `ADC result: A=0x${padHex(this.A, 2)}, cycles=${this.cycles}`);
    }

    public subtractWithCarry() {
        if (this.getFlag(Flag.Decimal)) this.logger.log(2, () => "Decimal mode set but ignored (NES)");
        const value = this.memory.read(this.addrResult.address);
        const oldA = this.A;
        const borrow = this.getFlag(Flag.Carry) ? 0 : 1;
        const result = this.A - value - borrow;
        const overflow = ((oldA ^ value) & (oldA ^ result) & 0x80) !== 0;
        this.logger.log(3, () => `SBC: A=0x${padHex(this.A, 2)}, value=0x${padHex(value, 2)}, borrow=${borrow}, result=0x${padHex(result & 0xFF, 2)}`);
        this.A = result & 0xFF;
        this.setFlag(Flag.Carry, result >= 0);
        this.setFlag(Flag.Overflow, overflow);
        this.updateZN(this.A);
        this.logger.log(2, () => `SBC result: A=0x${padHex(this.A, 2)}, cycles=${this.cycles}`);
    }

    public logicalOperation(operator: (a: number, b: number) => number) {
        const value = this.memory.read(this.addrResult.address);
        this.logger.log(3, () => `Logical op: A=0x${padHex(this.A, 2)}, value=0x${padHex(value, 2)}`);
        this.A = operator(this.A, value) & 0xFF;
        this.updateZN(this.A);
        this.logger.log(2, () => `Logical op result: A=0x${padHex(this.A, 2)}`);
    }

    public arithmeticShiftLeft() {
        const addr = this.addrResult.address;
        let value = addr === -1 ? this.A : this.memory.read(addr);
        this.logger.log(3, () => `ASL: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}`);
        this.setFlag(Flag.Carry, (value & 0x80) !== 0);
        value = (value << 1) & 0xFF;
        this.updateZN(value);
        if (addr === -1) {
            this.A = value;
            this.logger.log(2, () => `ASL (accumulator): A=0x${padHex(this.A, 2)}`);
        } else {
            this.memory.write(addr, value);
            this.logger.log(3, () => `ASL memory: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}`);
        }
    }

    public logicalShiftRight() {
        const addr = this.addrResult.address;
        let value = addr === -1 ? this.A : this.memory.read(addr);
        this.logger.log(3, () => `LSR: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}`);
        this.setFlag(Flag.Carry, (value & 0x01) !== 0);
        value = (value >> 1) & 0xFF;
        this.updateZN(value);
        if (addr === -1) {
            this.A = value;
            this.logger.log(2, () => `LSR (accumulator): A=0x${padHex(this.A, 2)}`);
        } else {
            this.memory.write(addr, value);
            this.logger.log(3, () => `LSR memory: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}`);
        }
    }

    public rotateLeft() {
        const addr = this.addrResult.address;
        let value = addr === -1 ? this.A : this.memory.read(addr);
        const carry = this.getFlag(Flag.Carry) ? 1 : 0;
        this.logger.log(3, () => `ROL: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}, carry=${carry}`);
        this.setFlag(Flag.Carry, (value & 0x80) !== 0);
        value = ((value << 1) | carry) & 0xFF;
        this.updateZN(value);
        if (addr === -1) {
            this.A = value;
            this.logger.log(2, () => `ROL (accumulator): A=0x${padHex(this.A, 2)}`);
        } else {
            this.memory.write(addr, value);
            this.logger.log(3, () => `ROL memory: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}`);
        }
    }

    public rotateRight() {
        const addr = this.addrResult.address;
        let value = addr === -1 ? this.A : this.memory.read(addr);
        const carry = this.getFlag(Flag.Carry) ? 0x80 : 0;
        this.logger.log(3, () => `ROR: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}, carry=${carry}`);
        this.setFlag(Flag.Carry, (value & 0x01) !== 0);
        value = ((value >> 1) | carry) & 0xFF;
        this.updateZN(value);
        if (addr === -1) {
            this.A = value;
            this.logger.log(2, () => `ROR (accumulator): A=0x${padHex(this.A, 2)}`);
        } else {
            this.memory.write(addr, value);
            this.logger.log(3, () => `ROR memory: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}`);
        }
    }

    public bitTest() {
        const value = this.memory.read(this.addrResult.address);
        this.logger.log(3, () => `BIT: addr=0x${padHex(this.addrResult.address, 4)}, value=0x${padHex(value, 2)}`);
        this.setFlag(Flag.Zero, (this.A & value) === 0);
        this.setFlag(Flag.Negative, (value & 0x80) !== 0);
        this.setFlag(Flag.Overflow, (value & 0x40) !== 0);
    }

    public jump() {
        this.logger.log(2, () => `JMP: PC=0x${padHex(this.PC, 4)} -> 0x${padHex(this.addrResult.address, 4)}`);
        this.PC = this.addrResult.address;
    }

    public jumpSubroutine() {
        const ret = this.PC - 1;
        this.logger.log(2, () => `JSR: Pushing return addr=0x${padHex(ret, 4)}, jumping to 0x${padHex(this.addrResult.address, 4)}`);
        this.memory.push((ret >> 8) & 0xFF);
        this.memory.push(ret & 0xFF);
        this.PC = this.addrResult.address;
    }

    public returnSubroutine() {
        const lo = this.memory.pull();
        const hi = this.memory.pull();
        this.PC = ((hi << 8) | lo) + 1;
        this.logger.log(2, () => `RTS: PC=0x${padHex(this.PC, 4)}`);
    }

    public returnInterrupt() {
        const oldSR = this.SR;
        this.SR = this.memory.pull() & 0xEF | Flag.Unused;
        const lo = this.memory.pull();
        const hi = this.memory.pull();
        this.PC = (hi << 8) | lo;
        this.logger.log(2, () => `RTI: SR=0x${padHex(oldSR, 2)} -> 0x${padHex(this.SR, 2)}, PC=0x${padHex(this.PC, 4)}`);
    }

    public break() {
        this.PC++;
        this.memory.push((this.PC >> 8) & 0xFF);
        this.memory.push(this.PC & 0xFF);
        this.memory.push(this.SR | Flag.Break | Flag.Unused);
        this.setFlag(Flag.InterruptDisable, true);
        this.PC = this.memory.read16(0xFFFE);
        this.logger.log(1, () => `BRK: Pushed PC=0x${padHex(this.PC - 1, 4)}, SR=0x${padHex(this.SR, 2)}, new PC=0x${padHex(this.PC, 4)}`);
    }

    public noOperation() {
        this.logger.log(2, () => `NOP`);
    }

    public branch(flag: Flag, expected: boolean) {
        const offset = this.memory.read(this.PC++);
        this.logger.log(3, () => `Branch: flag=${Flag[flag]}, expected=${expected}, offset=0x${padHex(offset, 2)}`);
        if (this.getFlag(flag) === expected) {
            const oldPC = this.PC;
            this.PC = (this.PC + (offset < 0x80 ? offset : offset - 0x100)) & 0xFFFF;
            this.cycles += (oldPC & 0xFF00) !== (this.PC & 0xFF00) ? 2 : 1;
            this.logger.log(2, () => `Branch taken: PC=0x${padHex(oldPC, 4)} -> 0x${padHex(this.PC, 4)}, cycles=${this.cycles}`);
        } else {
            this.logger.log(2, () => `Branch not taken: PC=0x${padHex(this.PC, 4)}`);
        }
    }

    public pushRegister(register: Register) {
        this.logger.log(3, () => `Push ${register}: value=0x${padHex(this[register], 2)}`);
        this.memory.push(this[register]);
    }

    public pullRegister(register: Register) {
        this[register] = this.memory.pull();
        this.logger.log(3, () => `Pull ${register}: value=0x${padHex(this[register], 2)}`);
        this.updateZN(this[register]);
        this.logger.log(2, () => `${register}=0x${padHex(this[register], 2)}`);
    }

    public pushStatus() {
        this.logger.log(3, () => `Push SR: value=0x${padHex(this.SR | Flag.Break | Flag.Unused, 2)}`);
        this.memory.push(this.SR | Flag.Break | Flag.Unused);
    }

    public pullStatus() {
        const oldSR = this.SR;
        this.SR = this.memory.pull() & 0xEF | Flag.Unused;
        this.logger.log(2, () => `Pull SR: 0x${padHex(oldSR, 2)} -> 0x${padHex(this.SR, 2)}`);
    }

    // Unofficial instruction implementations
    public loadAandX() {
        const value = this.memory.read(this.addrResult.address);
        this.logger.log(3, () => `LAX: addr=0x${padHex(this.addrResult.address, 4)}, value=0x${padHex(value, 2)}`);
        this.A = value;
        this.X = value;
        this.updateZN(value);
        this.logger.log(2, () => `LAX: A=0x${padHex(this.A, 2)}, X=0x${padHex(this.X, 2)}`);
    }

    public storeAandX() {
        const value = this.A & this.X;
        this.logger.log(3, () => `SAX: addr=0x${padHex(this.addrResult.address, 4)}, value=0x${padHex(value, 2)}`);
        this.memory.write(this.addrResult.address, value);
    }

    public decrementAndCompare() {
        const addr = this.addrResult.address;
        const oldValue = this.memory.read(addr);
        const value = (oldValue - 1) & 0xFF;
        this.logger.log(3, () => `DCP: addr=0x${padHex(addr, 4)}, value=0x${padHex(oldValue, 2)} -> 0x${padHex(value, 2)}`);
        this.memory.write(addr, value);
        const result = this.A - value;
        this.setFlag(Flag.Carry, this.A >= value);
        this.updateZN(result & 0xFF);
        this.logger.log(2, () => `DCP: A=0x${padHex(this.A, 2)}, result=0x${padHex(result & 0xFF, 2)}, cycles=${this.cycles}`);
    }

    public incrementAndSubtract() {
        const addr = this.addrResult.address;
        const oldValue = this.memory.read(addr);
        const value = (oldValue + 1) & 0xFF;
        this.logger.log(3, () => `ISB: addr=0x${padHex(addr, 4)}, value=0x${padHex(oldValue, 2)} -> 0x${padHex(value, 2)}`);
        this.memory.write(addr, value);
        const oldA = this.A;
        const borrow = this.getFlag(Flag.Carry) ? 0 : 1;
        const result = this.A - value - borrow;
        const overflow = ((oldA ^ value) & (oldA ^ result) & 0x80) !== 0;
        this.A = result & 0xFF;
        this.setFlag(Flag.Carry, result >= 0);
        this.setFlag(Flag.Overflow, overflow);
        this.updateZN(this.A);
        this.logger.log(2, () => `ISB: A=0x${padHex(oldA, 2)} -> 0x${padHex(this.A, 2)}, cycles=${this.cycles}`);
    }

    public shiftLeftOr() {
        const addr = this.addrResult.address;
        let value = this.memory.read(addr);
        this.logger.log(3, () => `SLO: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}`);
        this.setFlag(Flag.Carry, (value & 0x80) !== 0);
        value = (value << 1) & 0xFF;
        this.memory.write(addr, value);
        this.A |= value;
        this.updateZN(this.A);
        this.logger.log(2, () => `SLO: A=0x${padHex(this.A, 2)}, memory=0x${padHex(value, 2)}, cycles=${this.cycles}`);
    }

    public rotateLeftAnd() {
        const addr = this.addrResult.address;
        let value = this.memory.read(addr);
        const carry = this.getFlag(Flag.Carry) ? 1 : 0;
        this.logger.log(3, () => `RLA: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}, carry=${carry}`);
        this.setFlag(Flag.Carry, (value & 0x80) !== 0);
        value = ((value << 1) | carry) & 0xFF;
        this.memory.write(addr, value);
        this.A &= value;
        this.updateZN(this.A);
        this.logger.log(2, () => `RLA: A=0x${padHex(this.A, 2)}, memory=0x${padHex(value, 2)}, cycles=${this.cycles}`);
    }

    public rotateRightAdd() {
        const addr = this.addrResult.address;
        let value = this.memory.read(addr);
        const carry = this.getFlag(Flag.Carry) ? 0x80 : 0;
        this.logger.log(3, () => `RRA: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}, carry=${carry}`);
        this.setFlag(Flag.Carry, (value & 0x01) !== 0);
        value = ((value >> 1) | carry) & 0xFF;
        this.memory.write(addr, value);
        const oldA = this.A;
        const carryIn = this.getFlag(Flag.Carry) ? 1 : 0;
        const sum = this.A + value + carryIn;
        const overflow = ((this.A ^ sum) & (value ^ sum) & 0x80) !== 0;
        this.A = sum & 0xFF;
        this.setFlag(Flag.Carry, sum > 0xFF);
        this.setFlag(Flag.Overflow, overflow);
        this.updateZN(this.A);
        this.logger.log(2, () => `RRA: A=0x${padHex(oldA, 2)} -> 0x${padHex(this.A, 2)}, memory=0x${padHex(value, 2)}, cycles=${this.cycles}`);
    }

    /** SRE: LSR memory, then EOR with A. */
    public shiftRightEor() {
        const addr = this.addrResult.address;
        let value = this.memory.read(addr);
        this.logger.log(3, () => `SRE: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}`);
        this.setFlag(Flag.Carry, (value & 0x01) !== 0);
        value = (value >> 1) & 0xFF;
        this.memory.write(addr, value);
        this.A = (this.A ^ value) & 0xFF;
        this.updateZN(this.A);
        this.logger.log(2, () => `SRE: A=0x${padHex(this.A, 2)}, memory=0x${padHex(value, 2)}, cycles=${this.cycles}`);
    }

    public andRotateRight() {
        const value = this.memory.read(this.addrResult.address);
        this.logger.log(3, () => `ARR: addr=0x${padHex(this.addrResult.address, 4)}, value=0x${padHex(value, 2)}`);
        const carryIn = this.getFlag(Flag.Carry) ? 0x80 : 0;
        this.A = (((this.A & value) >> 1) | carryIn) & 0xFF;
        // Binary (non-decimal) ARR flag behavior: C = bit6, V = bit6 XOR bit5
        const bit6 = (this.A >> 6) & 1;
        const bit5 = (this.A >> 5) & 1;
        this.setFlag(Flag.Carry, bit6 === 1);
        this.setFlag(Flag.Overflow, (bit6 ^ bit5) === 1);
        this.updateZN(this.A);
        this.logger.log(2, () => `ARR: A=0x${padHex(this.A, 2)}`);
    }

    public compareXandA() {
        const value = this.memory.read(this.addrResult.address);
        const temp = (this.A & this.X) - value;
        this.logger.log(3, () => `AXS: addr=0x${padHex(this.addrResult.address, 4)}, value=0x${padHex(value, 2)}, A&X=0x${padHex(this.A & this.X, 2)}`);
        this.setFlag(Flag.Carry, (this.A & this.X) >= value);
        this.setFlag(Flag.Negative, (temp & 0x80) !== 0);
        this.setFlag(Flag.Zero, (temp & 0xFF) === 0);
        this.X = temp & 0xFF;
        this.logger.log(2, () => `AXS: X=0x${padHex(this.X, 2)}`);
    }

    public storeAandXHigh() {
        const addr = this.addrResult.address;
        const high = (addr >> 8) & 0xFF;
        const value = this.A & this.X & (high + 1);
        this.logger.log(3, () => `AHX: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}`);
        this.memory.write(addr, value);
    }

    public storeYHigh() {
        const addr = this.addrResult.address;
        const high = (addr >> 8) & 0xFF;
        const value = this.Y & (high + 1);
        this.logger.log(3, () => `SHY: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}`);
        this.memory.write(addr, value);
    }

    public storeXHigh() {
        const addr = this.addrResult.address;
        const high = (addr >> 8) & 0xFF;
        const value = this.X & (high + 1);
        this.logger.log(3, () => `SHX: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}`);
        this.memory.write(addr, value);
    }

    public transferAandXtoS() {
        const addr = this.addrResult.address;
        const high = (addr >> 8) & 0xFF;
        const value = this.A & this.X & (high + 1);
        this.logger.log(3, () => `TAS: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}`);
        this.memory.write(addr, value);
        this.SP = this.A & this.X;
        this.logger.log(2, () => `TAS: SP=0x${padHex(this.SP, 2)}`);
    }

    public loadAandXandS() {
        const value = this.memory.read(this.addrResult.address) & this.SP;
        this.logger.log(3, () => `LAS: addr=0x${padHex(this.addrResult.address, 4)}, value=0x${padHex(value, 2)}`);
        this.A = value;
        this.X = value;
        this.SP = value;
        this.updateZN(value);
        this.logger.log(2, () => `LAS: A=0x${padHex(this.A, 2)}, X=0x${padHex(this.X, 2)}, SP=0x${padHex(this.SP, 2)}`);
    }

    public andWithCarry() {
        const value = this.memory.read(this.addrResult.address);
        this.logger.log(3, () => `ANC: addr=0x${padHex(this.addrResult.address, 4)}, value=0x${padHex(value, 2)}`);
        this.A &= value;
        this.setFlag(Flag.Carry, (this.A & 0x80) !== 0);
        this.updateZN(this.A);
        this.logger.log(2, () => `ANC: A=0x${padHex(this.A, 2)}`);
    }

    public andShiftRight() {
        const value = this.memory.read(this.addrResult.address);
        this.logger.log(3, () => `ASR: addr=0x${padHex(this.addrResult.address, 4)}, value=0x${padHex(value, 2)}`);
        this.A &= value;
        this.setFlag(Flag.Carry, (this.A & 0x01) !== 0);
        this.A = this.A >> 1;
        this.updateZN(this.A);
        this.logger.log(2, () => `ASR: A=0x${padHex(this.A, 2)}`);
    }

    public transferXandAnd() {
        const value = this.memory.read(this.addrResult.address);
        this.logger.log(3, () => `XAA: addr=0x${padHex(this.addrResult.address, 4)}, value=0x${padHex(value, 2)}`);
        this.A = this.X & value;
        this.updateZN(this.A);
        this.logger.log(2, () => `XAA: A=0x${padHex(this.A, 2)}`);
    }

    public storeXandAHigh() {
        const addr = this.addrResult.address;
        const high = (addr >> 8) & 0xFF;
        const value = this.X & this.A & (high + 1);
        this.logger.log(3, () => `XAS: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}`);
        this.memory.write(addr, value);
    }

    public storeYandAHigh() {
        const addr = this.addrResult.address;
        const high = (addr >> 8) & 0xFF;
        const value = this.Y & this.A & (high + 1);
        this.logger.log(3, () => `YAS: addr=0x${padHex(addr, 4)}, value=0x${padHex(value, 2)}`);
        this.memory.write(addr, value);
    }

    public triggerIRQ() {
        if (!this.getFlag(Flag.InterruptDisable)) {
            this.memory.push((this.PC >> 8) & 0xFF);
            this.memory.push(this.PC & 0xFF);
            this.memory.push(this.SR | Flag.Unused);
            this.setFlag(Flag.InterruptDisable, true);
            this.PC = this.memory.read16(0xFFFE);
            this.cycles += 7;
            this.logger.log(1, () => `IRQ triggered: PC=0x${padHex(this.PC, 4)}, cycles=${this.cycles}`);
        } else {
            this.logger.log(2, () => `IRQ ignored: InterruptDisable set`);
        }
    }

    public triggerNMI() {
        this.memory.push((this.PC >> 8) & 0xFF);
        this.memory.push(this.PC & 0xFF);
        this.memory.push(this.SR | Flag.Unused);
        this.setFlag(Flag.InterruptDisable, true);
        this.PC = this.memory.read16(0xFFFA);
        this.cycles += 7;
        this.logger.log(1, () => `NMI triggered: PC=0x${padHex(this.PC, 4)}, cycles=${this.cycles}`);
    }

    step(): number {
        const startPC = this.PC;
        const opcode = this.memory.read(this.PC++);
        this.logger.log(3, () => `Fetch opcode: PC=0x${padHex(startPC, 4)}, opcode=0x${padHex(opcode, 2)}`);
        const inst = this.opcodes[opcode];
        if (!inst) {
            this.logger.log(1, () => `Unhandled opcode: 0x${padHex(opcode, 2)} at PC=0x${padHex(startPC, 4)}`);
            return 0;
        }
        this.logger.log(1, () => `Step: opcode=0x${padHex(opcode, 2)} at PC=0x${padHex(startPC, 4)}, cycles=${this.cycles}`);
        inst.mode();
        inst.exec();
        // +1 cycle on page cross ONLY for the read instructions flagged in
        // opcodes.json. Stores and read-modify-write ops never take it.
        const usedCycles = inst.cycles + (inst.pageCross && this.addrResult.pageCrossed ? 1 : 0);
        this.cycles += usedCycles;
        this.logger.log(2, () => `Step completed: cycles used=${usedCycles}, total cycles=${this.cycles}, state=${this.toString()}`);
        return usedCycles;
    }

    run(cycles: number): number {
        const endCycles = this.cycles + cycles;
        this.logger.log(1, () => `Running for ${cycles} cycles, target=${endCycles}`);
        while (this.cycles < endCycles) {
            this.step();
        }
        this.logger.log(1, () => `Run completed: total cycles=${this.cycles}`);
        return this.cycles;
    }

    /** Add wait cycles (e.g. OAM DMA stall). */
    public stall(cycles: number) {
        this.cycles += cycles;
    }

    getCycles(): number {
        this.logger.log(3, () => `Get cycles: ${this.cycles}`);
        return this.cycles;
    }

    toString(): string {
        const state = `PC:$${padHex(this.PC, 4)} A:$${padHex(this.A, 2)} ` +
            `X:$${padHex(this.X, 2)} Y:$${padHex(this.Y, 2)} ` +
            `SP:$${padHex(this.SP, 2)} SR:$${padHex(this.SR, 2)} ` +
            `Cycles:${this.cycles}`;
        this.logger.log(3, () => `State: ${state}`);
        return state;
    }
}